import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowRight, Sparkles, Sun, MoonStar } from "lucide-react";
import { authApi } from "../../lib/auth";
import { NATION_BY_SLUG, NATIONS_LIST, RARITY_STYLES, recordPersonalUnlock } from "../../lib/archive";

const RARITY_BORDER = { C: "#8C5A2B", B: "#B8B8B8", A: "#3F7BD0", S: "#C5A059" };

/**
 * Nation Signal — ONE free guess from your claimed nation, per user, ever.
 * After commit (right or wrong) it stays resolved. Wins go to personal cast
 * with NO global impact. Losses vanish — that figure cannot be reached again
 * via the nation signal (but can still be cracked on the global signal).
 */
export default function NationSignal({ user, onUnlock }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const claimedNation = user?.claimed_nation || "";
  const nationInfo = NATION_BY_SLUG[claimedNation];

  const load = async () => {
    setLoading(true);
    try {
      const res = await authApi.get("/me/nation-signal");
      setData(res.data);
    } catch { /* silent */ }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [claimedNation]);

  if (!claimedNation || !nationInfo) {
    return (
      <div className="bg-[#0A0A0A] border border-white/5 p-6 md:p-7" data-testid="nation-signal-empty">
        <div className="text-[10px] tracking-[0.32em] uppercase text-[#A39E99]">Your Nation Signal</div>
        <p className="font-display text-xl md:text-2xl text-[#EAE0D5] mt-3 leading-tight">
          Claim a nation to receive your one free signal.
        </p>
        <p className="text-sm text-[#A39E99] mt-3 leading-relaxed max-w-md">
          Each archive keeper is granted one bonus guess from the nation that claims them. Take the quiz first to unlock yours.
        </p>
        <a href="/nation-quiz" data-testid="nation-signal-quiz-cta" className="mt-4 inline-flex items-center gap-2 text-xs tracking-[0.24em] uppercase text-[#C5A059] hover:underline">
          Take the Rite <ArrowRight size={13} />
        </a>
      </div>
    );
  }

  if (loading) return null;

  // RESOLVED — WON state: show the unlocked record permanently
  if (data?.state === "won" && data?.character) {
    const r = data.character;
    const rstyle = RARITY_STYLES[r.rarity] || RARITY_STYLES.C;
    const border = RARITY_BORDER[r.rarity] || rstyle.color;
    return (
      <motion.div
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-[#0A0A0A] border-2 overflow-hidden"
        style={{ borderColor: `${border}66`, boxShadow: `0 0 60px -25px ${border}` }}
        data-testid="nation-signal-won"
      >
        <div className="flex items-center justify-between gap-3 px-5 py-3 border-b" style={{ borderColor: `${border}40`, background: `linear-gradient(90deg, ${border}24 0%, transparent 60%)` }}>
          <div className="inline-flex items-center gap-2 text-[10px] md:text-xs tracking-[0.32em] uppercase" style={{ color: border }}>
            <Sparkles size={14} /> Nation Signal — Claimed
          </div>
          <span className="text-[10px] tracking-[0.24em] uppercase text-[#737373] hidden sm:inline">One-shot bonus · spent</span>
        </div>
        <div className="grid md:grid-cols-12">
          <div className="md:col-span-5 aspect-square md:aspect-auto bg-[#050505] overflow-hidden">
            {r.image && <img src={r.image} alt={r.name} className="w-full h-full object-cover" style={{ objectPosition: r.image_position || "center" }} />}
          </div>
          <div className="md:col-span-7 p-6">
            <div className="text-[10px] tracking-[0.28em] uppercase" style={{ color: rstyle.color }}>{rstyle.label}</div>
            <h3 className="font-display text-2xl md:text-3xl text-[#EAE0D5] mt-2 leading-tight">{r.name}</h3>
            <div className="text-xs tracking-[0.22em] uppercase mt-1.5" style={{ color: nationInfo.accent }}>{nationInfo.name}</div>
            {r.quote && <p className="italic text-sm text-[#A39E99] mt-3 leading-relaxed">"{r.quote}"</p>}
            {r.bio && <p className="text-sm text-[#A39E99] mt-3 leading-relaxed line-clamp-3">{r.bio}</p>}
            <p className="text-[10px] tracking-[0.22em] uppercase text-[#737373] mt-5">
              The nation signal does not return. Hunt the rest through the global archive.
            </p>
          </div>
        </div>
      </motion.div>
    );
  }

  // RESOLVED — VANISHED state: respectful, somber lock-out
  if (data?.state === "vanished") {
    return (
      <div className="bg-[#0A0A0A] border border-white/10 p-7 md:p-9" data-testid="nation-signal-vanished">
        <div className="inline-flex items-center gap-2 text-[10px] tracking-[0.32em] uppercase text-[#A39E99]">
          <MoonStar size={12} /> Nation Signal — Lost
        </div>
        <p className="font-display text-xl md:text-2xl text-[#EAE0D5] mt-3 leading-tight">
          The figure dissolved into shadow.
        </p>
        <p className="text-sm text-[#A39E99] mt-3 leading-relaxed max-w-md">
          Your one free signal has been spent. The figure cannot be reached this way again — but the global archive still holds them. Crack them there.
        </p>
      </div>
    );
  }

  // EXHAUSTED state
  if (!data?.character && data?.reason === "exhausted") {
    return (
      <div
        className="bg-[#0A0A0A] border p-6 md:p-7"
        style={{ borderColor: `${nationInfo.accent}55`, boxShadow: `0 0 50px -25px ${nationInfo.accent}` }}
        data-testid="nation-signal-exhausted"
      >
        <div className="text-[10px] tracking-[0.32em] uppercase" style={{ color: nationInfo.accent }}>
          {nationInfo.name} — Already Yours
        </div>
        <p className="font-display text-xl md:text-2xl text-[#EAE0D5] mt-3 leading-tight">
          You've already claimed every figure your nation can offer.
        </p>
      </div>
    );
  }

  const ch = data?.character;
  if (!ch) return null;

  // SERVED but not yet committed — the actual guess UI
  const onGuess = async (e) => {
    e?.preventDefault?.();
    const fd = new FormData(e.target);
    const picked = String(fd.get("pick") || "").toLowerCase();
    if (!picked) { toast.error("Pick a nation to commit."); return; }
    setBusy(true);
    try {
      const res = await authApi.post("/me/nation-signal/guess", { character_id: ch.id, nation: picked });
      if (res.data.correct) {
        recordPersonalUnlock(res.data.character);
        onUnlock && onUnlock(res.data.character);
        toast.success(res.data.first_discovery ? `NEW RECORD RECOVERED — ${res.data.character.name}.` : `${res.data.character.name} added.`);
      } else if (res.data.vanished) {
        toast.error("The figure vanishes. Your one free signal is spent.");
      }
      await load(); // refetch to surface the resolved state
    } catch (err) {
      toast.error("Signal could not resolve. Try again.");
    } finally { setBusy(false); }
  };

  return (
    <div
      className="bg-[#0A0A0A] border p-6 md:p-7"
      style={{ borderColor: `${nationInfo.accent}55`, boxShadow: `0 0 50px -25px ${nationInfo.accent}` }}
      data-testid="nation-signal-locked"
    >
      <div className="flex items-center justify-between gap-3 mb-2 flex-wrap">
        <div className="inline-flex items-center gap-2 text-[10px] tracking-[0.32em] uppercase" style={{ color: nationInfo.accent }}>
          <Sun size={12} /> Your One Free Signal · {nationInfo.name}
        </div>
        <span className="text-[10px] tracking-[0.22em] uppercase text-[#737373]">No global impact</span>
      </div>
      <p className="text-[11px] tracking-[0.18em] uppercase text-[#737373] mb-5">
        One bonus character. One commit. After this, you'll work for the rest.
      </p>

      <div className="grid md:grid-cols-12 gap-6">
        <div className="md:col-span-5">
          <div
            className="relative aspect-[3/4] bg-[#050505] overflow-hidden border"
            style={{ borderColor: `${nationInfo.accent}44` }}
          >
            {ch.image && (
              <img
                src={ch.image}
                alt="Locked figure"
                className="w-full h-full object-cover"
                style={{
                  objectPosition: ch.image_position || "center",
                  filter: "blur(14px) brightness(0.55) saturate(0.85)",
                  transform: "scale(1.1)",
                }}
              />
            )}
            <div className="absolute inset-0 flex items-center justify-center p-5 text-center">
              <p className="italic text-sm md:text-base text-[#EAE0D5] leading-relaxed drop-shadow-[0_1px_8px_rgba(0,0,0,0.9)]">
                "{ch.quote}"
              </p>
            </div>
          </div>
        </div>
        <form onSubmit={onGuess} className="md:col-span-7 flex flex-col">
          <p className="text-sm text-[#A39E99] leading-relaxed">
            A figure from <span className="font-medium" style={{ color: nationInfo.accent }}>{nationInfo.name}</span> watches. Claim them by committing — carefully.
            One wrong commit, and the figure dissolves forever from this path.
          </p>
          <div className="mt-4 grid grid-cols-3 gap-2" data-testid="nation-signal-picks">
            {NATIONS_LIST.map((n) => (
              <label
                key={n.slug}
                className="cursor-pointer text-[10px] tracking-[0.24em] uppercase border px-2 py-2 text-center hover:border-[#C5A059]/60 has-[:checked]:bg-[#C5A059]/15 has-[:checked]:border-[#C5A059] has-[:checked]:text-[#EAE0D5] border-white/10 text-[#A39E99]"
                data-testid={`nation-signal-pick-${n.slug}`}
              >
                <input type="radio" name="pick" value={n.slug} className="hidden" />
                {n.name}
              </label>
            ))}
          </div>
          <button
            type="submit"
            disabled={busy}
            data-testid="nation-signal-commit-btn"
            className="mt-5 self-start inline-flex items-center gap-2 bg-[#C5A059] text-black px-5 py-3 text-[11px] tracking-[0.26em] uppercase hover:bg-[#D4AF68] disabled:opacity-50"
          >
            {busy ? "Sealing…" : "Commit (One Shot)"}
          </button>
        </form>
      </div>
    </div>
  );
}
